/**
 * Pure helpers for the FinOps Alerts UI (panel + page). No React here.
 *
 * Dates arrive as microsecond ISO UTC (`2027-02-28T00:00:00.000000Z`). They are
 * sliced, never fed to `new Date()` — some Safari versions reject more than three
 * fractional digits — and every day count is in UTC, like the rest of the module.
 */

import type {
  FinopsAlertSeverity,
  FinopsAlertStatusFilter,
  FinopsAlertsFilters,
} from '../../../../shared/types/finops';

/** Deny key gating the Alerts route, its nav item and the Overview panel. */
export const FINOPS_ALERTS_SUBTAB = 'subtab:finops:alerts';

export const ALERTS_PATH = '/technology/finops/alerts';

/** The id is `${eventArn}|${affectedAccount}` — URLSearchParams escapes `|`, `:` and `/`. */
export const alertHref = (id: string) => `${ALERTS_PATH}?${new URLSearchParams({ alert: id })}`;

// ─── Filters ⇄ URL ───────────────────────────────────────────────────────────

export type AlertsFilterState = FinopsAlertsFilters & { status: FinopsAlertStatusFilter };

const STATUS_VALUES: readonly FinopsAlertStatusFilter[] = ['open', 'resolved', 'all'];
export const SEVERITY_VALUES: readonly FinopsAlertSeverity[] = ['critical', 'warning', 'info'];
// Same rules as the router's zod input, so a hand-edited URL never becomes a BAD_REQUEST.
const SERVICE_RE = /^[A-Z0-9_]{1,64}$/;
const ACCOUNT_RE = /^\d{12}$/;

/** Always carries `status`; unset filters are absent (not undefined) so the query key is stable. */
export function parseAlertFilters(sp: URLSearchParams): AlertsFilterState {
  const status = sp.get('status');
  const severity = sp.get('severity');
  const service = sp.get('service');
  const account = sp.get('account');
  const f: AlertsFilterState = {
    status: STATUS_VALUES.includes(status as FinopsAlertStatusFilter)
      ? (status as FinopsAlertStatusFilter)
      : 'open',
  };
  if (SEVERITY_VALUES.includes(severity as FinopsAlertSeverity)) f.severity = severity as FinopsAlertSeverity;
  if (service && SERVICE_RE.test(service)) f.service = service;
  if (account && ACCOUNT_RE.test(account)) f.accountId = account;
  return f;
}

/** Defaults stay out of the URL. `alert` is never carried over — a filter change drops the focus. */
export function filtersToParams(f: AlertsFilterState): URLSearchParams {
  const sp = new URLSearchParams();
  if (f.status !== 'open') sp.set('status', f.status);
  if (f.severity) sp.set('severity', f.severity);
  if (f.service) sp.set('service', f.service);
  if (f.accountId) sp.set('account', f.accountId);
  return sp;
}

export const isDefaultFilters = (f: AlertsFilterState) =>
  f.status === 'open' && !f.severity && !f.service && !f.accountId;

export type AlertsEmptyKind = 'feed_inactive' | 'no_open' | 'no_results';

/** Only meaningful when the list came back empty. A feed that never started wins over any filter. */
export function alertsEmptyKind(feedSince: string | null | undefined, f: AlertsFilterState): AlertsEmptyKind {
  if (!feedSince) return 'feed_inactive';
  return isDefaultFilters(f) ? 'no_open' : 'no_results';
}

// ─── Dates ───────────────────────────────────────────────────────────────────

const utcDay = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return Date.UTC(y, m - 1, d);
};

/** Whole UTC calendar days from today to the ISO date: 0 today, >0 future, <0 past. */
export function deadlineDays(iso: string, now: Date = new Date()): number {
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((utcDay(iso) - today) / 86_400_000);
}

/** '2027-02-28T00:00:00.000000Z' → 'Feb 28, 2027' (UTC). */
export const fmtDateUTC = (iso: string) =>
  new Date(utcDay(iso)).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });

// ─── Severity legend ─────────────────────────────────────────────────────────

/**
 * Literal keys (not built from the rule id) so audit-i18n can resolve them. A rule
 * added later in shared/types without an entry here falls back to its English label.
 */
export const RULE_LABEL_KEYS: Record<string, string> = {
  extended_support: 'finops.alerts.legend.extended_support',
  billing_persona: 'finops.alerts.legend.billing_persona',
  action_required: 'finops.alerts.legend.action_required',
  scheduled_change: 'finops.alerts.legend.scheduled_change',
};
